import { observer } from "mobx-react-lite";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import React from "react";
import { useRouter } from "next/router";
import { CartUpsellConfig } from "../../config/cartUpsells";
import CartStore from "../../stores/CartStore";
import AuthStore from "../../stores/AuthStore";

type Props = {
    cartStore: CartStore | null;
    upsellConfig: CartUpsellConfig;
};

const CartUpsellDialog = observer(({ cartStore, upsellConfig }: Props) => {
    const theme = useTheme();
    const router = useRouter();
    const [open, setOpen] = React.useState(false);
    const [isAdding, setIsAdding] = React.useState(false);

    React.useEffect(() => {
        // Check if we should show the dialog when component mounts or cart changes
        if (cartStore && cartStore.cart) {
            const shouldShow = upsellConfig.shouldShow(
                cartStore.cart,
                AuthStore.boxoffice,
            );
            setOpen(shouldShow);
        }
    }, [cartStore, cartStore?.cart, upsellConfig]);

    const handleClose = () => {
        setOpen(false);
    };

    const handleAdd = async () => {
        // Link-based upsell: navigate instead of adding to cart.
        if (upsellConfig.href) {
            upsellConfig.onAdd?.();
            handleClose();
            router.push(upsellConfig.href);
            return;
        }

        if (!cartStore || !cartStore.cart || !upsellConfig.getDepartmentId) {
            return;
        }

        const departmentId = upsellConfig.getDepartmentId(cartStore.cart);
        if (departmentId === null) {
            handleClose();
            return;
        }

        setIsAdding(true);

        try {
            const quantity = upsellConfig.quantity || 1;

            for (let i = 0; i < quantity; i++) {
                await cartStore.incrementGaQuantity(departmentId);
            }

            if (upsellConfig.onAdd) {
                upsellConfig.onAdd();
            }

            handleClose();
        } catch (err) {
            console.error("Error adding upsell item to cart:", err);
        } finally {
            setIsAdding(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            aria-labelledby="cart-upsell-dialog-title"
            aria-describedby="cart-upsell-dialog-description"
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    overflow: "hidden",
                },
            }}
        >
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    color: theme.palette.primary.contrastText,
                    px: 3,
                    py: 2.5,
                }}
            >
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                        sx={{
                            bgcolor: "rgba(255,255,255,0.2)",
                            borderRadius: "50%",
                            width: 44,
                            height: 44,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        <LocalOfferOutlinedIcon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant="overline"
                            sx={{
                                opacity: 0.85,
                                lineHeight: 1,
                                letterSpacing: 1.2,
                                display: "block",
                            }}
                        >
                            Special offer
                        </Typography>
                        <DialogTitle
                            id="cart-upsell-dialog-title"
                            sx={{
                                p: 0,
                                color: "inherit",
                                fontSize: "1.25rem",
                                fontWeight: 600,
                                lineHeight: 1.3,
                            }}
                        >
                            {upsellConfig.title}
                        </DialogTitle>
                    </Box>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        size="small"
                        disabled={isAdding}
                        sx={{
                            color: "inherit",
                            opacity: 0.8,
                            "&:hover": { opacity: 1 },
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>
            </Box>
            <DialogContent sx={{ px: 3, py: 3 }}>
                <Typography
                    id="cart-upsell-dialog-description"
                    variant="body1"
                    color="text.primary"
                >
                    {upsellConfig.message}
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    color="inherit"
                    disabled={isAdding}
                >
                    {upsellConfig.cancelText || "No Thanks"}
                </Button>
                <Button
                    onClick={handleAdd}
                    variant="contained"
                    color="primary"
                    disableElevation
                    disabled={isAdding}
                    autoFocus
                >
                    {isAdding
                        ? "Adding..."
                        : upsellConfig.confirmText || "Add to Cart"}
                </Button>
            </DialogActions>
        </Dialog>
    );
});

export default CartUpsellDialog;
